import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Autocomplete } from '../components/Autocomplete';
import { EditableEntityTable } from '../components/EditableEntityTable';
import { PaginatedTable } from '../components/PaginatedTable';
import { SharedScrapeMetadataPanel } from '../components/SharedScrapeMetadataPanel';
import type {
  EditableFieldSpec,
  EditableFieldValue,
  EntityDetailRow,
} from '../components/EditableEntityTable';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import {
  getApiErrorMessage,
  getProblemDetailMessage,
} from '@quoruum/frontend-common/api/errorMessage';
import {
  companyQueryOptions,
  companyGetOptions,
  companySaveMutation,
  hotelQueryOptions,
  locationGetOptions,
  locationQueryOptions,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import {
  BOOKING_ENGINE_TYPE_OPTIONS,
  isBookingEngineType,
  type BookingEngineType,
} from '../constants/bookingEngines';
import { HOTEL_SITES } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import type { HotelSite } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import type { CompanyBean, HotelBean } from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import { hotelActionStatusesOptions } from '@quoruum/frontend-common/oas/gen/admin/@tanstack/react-query.gen';
import { HotelStatusBean } from '@quoruum/frontend-common/oas/gen/admin/types.gen';

const COMPANY_SEARCH_DEBOUNCE_MS = 250;
const MIN_COMPANY_SEARCH_LENGTH = 2;
const CITY_SEARCH_DEBOUNCE_MS = 250;
const MIN_CITY_SEARCH_LENGTH = 2;
const COMPANY_ID_PARAM = 'companyId';
const COMPANY_TAB_PARAM = 'companyTab';
const COMPANY_CITY_ID_PARAM = 'companyCityId';
const HOTELS_BOOKING_ENGINE_PARAM = 'hotelsBookingEngine';
const HOTELS_SITE_PARAM = 'hotelsSite';
const HOTELS_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DEFAULT_HOTELS_PAGE_SIZE = 25;

type CompanyOption = {
  id: string;
  name: string;
};

type CityOption = {
  id: string;
  label: string;
};

type CompanyDetailsTabKey = 'COMPANY' | 'HOTELS' | `SHARED_SCRAPE_METADATA:${BookingEngineType}`;

type EditableCompanyFieldKey = 'homePageHost' | 'bookingEngineTypes';

type TextEditableFieldDefinition = {
  key: EditableCompanyFieldKey;
  label: string;
  editType: 'text';
  getValue: (company: CompanyBean) => string;
  renderReadOnlyValue: (value: string) => ReactNode;
  buildMutationBody: (companyId: string, value: string) => CompanyBean;
};

type MultiSelectEnumFieldDefinition = {
  key: EditableCompanyFieldKey;
  label: string;
  editType: 'multiselect-enum';
  options: ReadonlyArray<string>;
  getValue: (company: CompanyBean) => string[];
  renderReadOnlyValue: (value: string[]) => ReactNode;
  buildMutationBody: (companyId: string, value: string[]) => CompanyBean;
};

type EditableCompanyFieldDefinition = TextEditableFieldDefinition | MultiSelectEnumFieldDefinition;

const EDITABLE_COMPANY_FIELD_DEFINITIONS: Array<EditableCompanyFieldDefinition> = [
  {
    key: 'bookingEngineTypes',
    label: 'bookingEngineTypes',
    editType: 'multiselect-enum',
    options: BOOKING_ENGINE_TYPE_OPTIONS,
    getValue: (company) => company.bookingEngineTypes ?? [],
    renderReadOnlyValue: (value) =>
      value.length === 0 ? (
        '-'
      ) : (
        <ul className="list-none space-y-0.5">
          {value.map((item) => (
            <li key={item} className="text-xs text-slate-800">
              {item}
            </li>
          ))}
        </ul>
      ),
    buildMutationBody: (companyId, value) => ({
      id: companyId,
      bookingEngineTypes: value as BookingEngineType[],
    }),
  },
];

const EDITABLE_COMPANY_FIELD_BY_KEY = new Map(
  EDITABLE_COMPANY_FIELD_DEFINITIONS.map((f) => [f.key, f] as const)
);

const COMPANY_EDITABLE_FIELD_SPECS: ReadonlyMap<EditableCompanyFieldKey, EditableFieldSpec> =
  new Map(
    EDITABLE_COMPANY_FIELD_DEFINITIONS.map((def) => [
      def.key,
      def.editType === 'multiselect-enum'
        ? { editType: 'multiselect-enum', options: def.options }
        : { editType: 'text' },
    ])
  );

function toSharedMetadataTabKey(bookingEngineType: BookingEngineType): CompanyDetailsTabKey {
  return `SHARED_SCRAPE_METADATA:${bookingEngineType}`;
}

function toSharedMetadataTabBookingEngine(tabKey: CompanyDetailsTabKey): BookingEngineType | null {
  if (!tabKey.startsWith('SHARED_SCRAPE_METADATA:')) {
    return null;
  }

  const maybeBookingEngineType = tabKey.slice('SHARED_SCRAPE_METADATA:'.length);
  return isBookingEngineType(maybeBookingEngineType) ? maybeBookingEngineType : null;
}

function normalizeCompanyTabParam(tabParam: string | null): string | null {
  if (!tabParam) {
    return tabParam;
  }

  const trimmedTabParam = tabParam.trim();
  if (!trimmedTabParam.includes('%')) {
    return trimmedTabParam;
  }

  try {
    // Handle values that were encoded before being put into the query param.
    return decodeURIComponent(trimmedTabParam);
  } catch {
    return trimmedTabParam;
  }
}

function parseCompanyDetailsTab(tabParam: string | null): CompanyDetailsTabKey {
  const normalizedTabParam = normalizeCompanyTabParam(tabParam);

  if (normalizedTabParam === 'COMPANY' || normalizedTabParam === 'HOTELS') {
    return normalizedTabParam;
  }

  if (normalizedTabParam && normalizedTabParam.startsWith('SHARED_SCRAPE_METADATA:')) {
    const maybeBookingEngineType = normalizedTabParam.slice('SHARED_SCRAPE_METADATA:'.length);
    if (isBookingEngineType(maybeBookingEngineType)) {
      return toSharedMetadataTabKey(maybeBookingEngineType);
    }
  }

  return 'COMPANY';
}

function formatUnknownValue(value: unknown): ReactNode {
  if (value === null || value === undefined) {
    return '-';
  }

  if (typeof value === 'string') {
    return value || '-';
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (
    Array.isArray(value) &&
    value.every(
      (item) => typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean'
    )
  ) {
    if (value.length === 0) return '-';
    return (
      <ul className="list-none space-y-0.5">
        {value.map((item, index) => (
          <li key={index} className="text-xs text-slate-800">
            {String(item)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <SyntaxHighlighter
      language="json"
      customStyle={{
        margin: 0,
        padding: '0.5rem',
        borderRadius: '0.375rem',
        fontSize: '0.7rem',
        lineHeight: 1.4,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        overflowX: 'scroll',
      }}
      wrapLongLines
    >
      {JSON.stringify(value, null, 2)}
    </SyntaxHighlighter>
  );
}

function formatHotelAddress(hotel: HotelBean): string {
  const parts = [hotel.address1, hotel.address2, hotel.city?.name].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0
  );
  return parts.join(', ') || '-';
}

function buildHotelMapHref(hotelId: string | undefined): string {
  if (!hotelId) {
    return '/tools/hotel-explorer';
  }

  const params = new URLSearchParams();
  params.set('searchMode', 'HOTEL');
  params.set('selectedId', hotelId);
  params.set('drawerTab', 'HOTEL');
  return `/tools/hotel-explorer?${params.toString()}`;
}

function formatCityLabel(city: {
  name?: string;
  parent?: { code?: string; parent?: { code?: string } };
}): string {
  const cityName = typeof city.name === 'string' ? city.name : '';
  const suffixParts = [city.parent?.code, city.parent?.parent?.code].filter(
    (part): part is string => typeof part === 'string' && part.trim().length > 0
  );
  const suffix = suffixParts.length > 0 ? ` (${suffixParts.join(', ')})` : '';
  return `${cityName}${suffix}`;
}

export function Companies(): ReactElement {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialDetailsTab = parseCompanyDetailsTab(searchParams.get(COMPANY_TAB_PARAM));
  const initialSelectedCityId = searchParams.get(COMPANY_CITY_ID_PARAM)?.trim() || null;
  const initialBookingEngineFilter = ((): BookingEngineType | '' => {
    const v = searchParams.get(HOTELS_BOOKING_ENGINE_PARAM)?.trim() ?? '';
    return isBookingEngineType(v) ? v : '';
  })();
  const initialSiteFilter = ((): HotelSite | '' => {
    const v = searchParams.get(HOTELS_SITE_PARAM)?.trim() ?? '';
    return (HOTEL_SITES as readonly string[]).includes(v) ? (v as HotelSite) : '';
  })();
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState('');
  const [activeDetailsTab, setActiveDetailsTab] = useState<CompanyDetailsTabKey>(initialDetailsTab);
  const [hotelsPage, setHotelsPage] = useState(1);
  const [hotelsPageSize, setHotelsPageSize] = useState<number>(DEFAULT_HOTELS_PAGE_SIZE);
  const [bookingEngineFilter, setBookingEngineFilter] = useState<BookingEngineType | ''>(
    initialBookingEngineFilter
  );
  const [siteFilter, setSiteFilter] = useState<HotelSite | ''>(initialSiteFilter);
  const [citySearch, setCitySearch] = useState('');
  const [debouncedCitySearch, setDebouncedCitySearch] = useState('');
  const [selectedCityId, setSelectedCityId] = useState<string | null>(initialSelectedCityId);
  const hasInitializedSearchRef = useRef(false);
  const previousSelectedCompanyIdRef = useRef<string | null | undefined>(undefined);

  const selectedCompanyId = searchParams.get(COMPANY_ID_PARAM) ?? null;

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedCompanySearch(companySearch.trim());
    }, COMPANY_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [companySearch]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedCitySearch(citySearch.trim());
    }, CITY_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [citySearch]);

  const { data: companiesQueryResponse } = useQuery({
    ...companyQueryOptions(
      debouncedCompanySearch.length >= MIN_COMPANY_SEARCH_LENGTH
        ? { query: { search: debouncedCompanySearch } }
        : undefined
    ),
    enabled: debouncedCompanySearch.length >= MIN_COMPANY_SEARCH_LENGTH,
  });

  const {
    data: selectedCompany,
    isLoading: isSelectedCompanyLoading,
    isError: hasSelectedCompanyError,
    error: selectedCompanyError,
  } = useQuery({
    ...companyGetOptions({ path: { id: selectedCompanyId! } }),
    enabled: selectedCompanyId !== null,
  });

  const selectedCompanyBookingEngineTypes = useMemo<Array<BookingEngineType>>(() => {
    if (!selectedCompany?.bookingEngineTypes) {
      return [];
    }

    return selectedCompany.bookingEngineTypes.filter((item): item is BookingEngineType =>
      isBookingEngineType(item)
    );
  }, [selectedCompany?.bookingEngineTypes]);

  const {
    data: hotelsQueryResponse,
    isLoading: isHotelsLoading,
    isError: hasHotelsError,
    error: hotelsError,
    isFetching: isHotelsFetching,
  } = useQuery({
    ...hotelQueryOptions({
      query: {
        'brand.id': selectedCompanyId ?? undefined,
        'location.city.id': selectedCityId ?? undefined,
        bookingEngine: bookingEngineFilter || undefined,
        site: siteFilter || undefined,
        page: hotelsPage,
        pageSize: hotelsPageSize,
      },
    }),
    enabled: activeDetailsTab === 'HOTELS' && selectedCompanyId !== null,
    placeholderData: (previousData) => previousData,
  });

  const { data: cityQueryResponse } = useQuery({
    ...(debouncedCitySearch.length >= MIN_CITY_SEARCH_LENGTH
      ? locationQueryOptions({
          query: {
            search: debouncedCitySearch,
            pageSize: 8,
          },
        })
      : locationQueryOptions()),
    enabled: activeDetailsTab === 'HOTELS' && debouncedCitySearch.length >= MIN_CITY_SEARCH_LENGTH,
    placeholderData: (previousData) => previousData,
  });

  const { data: selectedCityFromIdResponse } = useQuery({
    ...locationGetOptions({ path: { id: selectedCityId ?? '' } }),
    enabled:
      activeDetailsTab === 'HOTELS' && selectedCityId !== null && citySearch.trim().length === 0,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (selectedCompany?.name && !hasInitializedSearchRef.current) {
      hasInitializedSearchRef.current = true;
      setCompanySearch(selectedCompany.name);
    }
  }, [selectedCompany?.name]);

  const companyOptions = useMemo<Array<CompanyOption>>(() => {
    return (
      companiesQueryResponse?.results
        ?.filter(
          (c): c is typeof c & { id: string; name: string } =>
            typeof c.id === 'string' && typeof c.name === 'string'
        )
        .map((c) => ({ id: c.id, name: c.name })) ?? []
    );
  }, [companiesQueryResponse]);

  const cityOptions = useMemo<Array<CityOption>>(() => {
    return (
      cityQueryResponse?.results
        ?.filter((city): city is typeof city & { id: string; name: string } => {
          return typeof city.id === 'string' && typeof city.name === 'string';
        })
        .map((city) => ({
          id: city.id,
          label: formatCityLabel(city),
        })) ?? []
    );
  }, [cityQueryResponse]);

  useEffect(() => {
    if (!selectedCityId || !selectedCityFromIdResponse?.id || !selectedCityFromIdResponse.name) {
      return;
    }
    if (selectedCityFromIdResponse.id !== selectedCityId) {
      return;
    }

    const label = formatCityLabel(selectedCityFromIdResponse);
    if (!label) {
      return;
    }

    setCitySearch((previousValue) => (previousValue === label ? previousValue : label));
    setDebouncedCitySearch((previousValue) => (previousValue === label ? previousValue : label));
  }, [selectedCityFromIdResponse, selectedCityId]);

  const hotelsRows = useMemo(
    () => hotelsQueryResponse?.results ?? [],
    [hotelsQueryResponse?.results]
  );

  const hotelIdsForStatusesQuery = useMemo(
    () =>
      hotelsRows
        .map((hotel) => hotel.id)
        .filter((hotelId): hotelId is string => typeof hotelId === 'string' && hotelId.length > 0),
    [hotelsRows]
  );

  const {
    data: hotelsStatusesResponse,
    isLoading: isHotelsStatusesLoading,
    isFetching: isHotelsStatusesFetching,
    isError: hasHotelsStatusesError,
  } = useQuery({
    ...hotelActionStatusesOptions({
      query: {
        id: hotelIdsForStatusesQuery,
      },
    }),
    enabled: activeDetailsTab === 'HOTELS' && hotelIdsForStatusesQuery.length > 0,
    staleTime: 60 * 1000,
  });

  const hotelsStatusById = useMemo(() => {
    const statusMap = new Map<string, HotelStatusBean>();
    const hotelStatuses = (hotelsStatusesResponse?.results ?? []) as Array<unknown>;

    for (const status of hotelStatuses) {
      if (!status || typeof status !== 'object') {
        continue;
      }

      const statusId = (status as { id?: unknown }).id;
      if (typeof statusId === 'string' && statusId.length > 0) {
        statusMap.set(statusId, status);
      }
    }
    return statusMap;
  }, [hotelsStatusesResponse?.results]);

  const hotelsHasNextPage = typeof hotelsQueryResponse?.nextPage === 'number';

  const hotelsColumns = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        cellClassName: 'text-slate-900',
        renderCell: (hotel: HotelBean) =>
          hotel.id ? (
            <Link
              to={buildHotelMapHref(hotel.id)}
              className="text-sky-700 underline decoration-sky-300 underline-offset-2 hover:text-sky-800"
            >
              {hotel.name || hotel.id}
            </Link>
          ) : (
            hotel.name || '-'
          ),
      },
      {
        key: 'homePage',
        header: 'Home Page',
        cellClassName: 'break-all',
        renderCell: (hotel: HotelBean) => hotel.homePageUri || '-',
      },
      {
        key: 'address',
        header: 'Address',
        renderCell: (hotel: HotelBean) => formatHotelAddress(hotel),
      },
      {
        key: 'bookingEngine',
        header: 'Booking Engine',
        renderCell: (hotel: HotelBean) => hotel.bookingEngineType || '-',
      },
      {
        key: 'externalIds',
        header: 'External IDs',
        renderCell: (hotel: HotelBean) =>
          hotel.externalIds && hotel.externalIds.length > 0 ? (
            <ul className="list-disc list-inside">
              {hotel.externalIds.map((eid) => (
                <li key={`${eid.site}:${eid.id}`}>
                  {eid.site}:{eid.id}
                </li>
              ))}
            </ul>
          ) : (
            '-'
          ),
      },
      {
        key: 'brandMostRecentShopTime',
        header: 'BRAND Most Recent Shop Time',
        renderCell: (hotel: HotelBean) => {
          const hotelStatus = hotel.id ? hotelsStatusById.get(hotel.id) : undefined;
          const brandStatus = hotelStatus?.rateScrapeMetadataStatus?.BRAND;

          if (!hotel.id) {
            return '-';
          }

          if (hasHotelsStatusesError) {
            return 'Error';
          }

          if (isHotelsStatusesLoading || isHotelsStatusesFetching) {
            return 'Loading...';
          }

          return brandStatus?.mostRecentShopTime || '-';
        },
      },
      {
        key: 'brandShouldRateShopWithRecency',
        header: 'BRAND Should Rate Shop With Recency',
        renderCell: (hotel: HotelBean) => {
          const hotelStatus = hotel.id ? hotelsStatusById.get(hotel.id) : undefined;
          const brandStatus = hotelStatus?.rateScrapeMetadataStatus?.BRAND;

          if (!hotel.id) {
            return '-';
          }

          if (hasHotelsStatusesError) {
            return 'Error';
          }

          if (isHotelsStatusesLoading || isHotelsStatusesFetching) {
            return 'Loading...';
          }

          return typeof brandStatus?.shouldRateShopWithRecency === 'boolean'
            ? String(brandStatus.shouldRateShopWithRecency)
            : '-';
        },
      },
    ],
    [hasHotelsStatusesError, hotelsStatusById, isHotelsStatusesFetching, isHotelsStatusesLoading]
  );

  useEffect(() => {
    if (previousSelectedCompanyIdRef.current === undefined) {
      previousSelectedCompanyIdRef.current = selectedCompanyId;
      return;
    }

    const previousSelectedCompanyId = previousSelectedCompanyIdRef.current;
    previousSelectedCompanyIdRef.current = selectedCompanyId;

    if (previousSelectedCompanyId === selectedCompanyId) {
      return;
    }

    setHotelsPage(1);
    setCitySearch('');
    setDebouncedCitySearch('');
    setSelectedCityId(null);
  }, [selectedCompanyId]);

  useEffect(() => {
    setHotelsPage(1);
  }, [selectedCityId]);

  type SaveCompanyMutationOptions = Parameters<typeof useMutation>[0];
  const createSaveCompanyMutation =
    companySaveMutation as unknown as () => SaveCompanyMutationOptions;
  const saveCompanyMutation = useMutation(createSaveCompanyMutation());

  const detailRows = useMemo<Array<EntityDetailRow<EditableCompanyFieldKey>>>(() => {
    if (!selectedCompany) return [];
    const includedEditableFields = new Set<EditableCompanyFieldKey>();
    const rows: Array<EntityDetailRow<EditableCompanyFieldKey>> = [];

    for (const [key, value] of Object.entries(selectedCompany)) {
      const editableField = EDITABLE_COMPANY_FIELD_BY_KEY.get(key as EditableCompanyFieldKey);
      if (editableField) {
        if (editableField.editType === 'text') {
          const editableFieldValue = editableField.getValue(selectedCompany);
          rows.push({
            key: editableField.label,
            value: editableField.renderReadOnlyValue(editableFieldValue),
            editableFieldKey: editableField.key,
            editableFieldValue,
          });
        } else {
          const editableFieldArrayValue = editableField.getValue(selectedCompany);
          rows.push({
            key: editableField.label,
            value: editableField.renderReadOnlyValue(editableFieldArrayValue),
            editableFieldKey: editableField.key,
            editableFieldArrayValue,
          });
        }
        includedEditableFields.add(editableField.key);
        continue;
      }
      rows.push({ key, value: formatUnknownValue(value) });
    }

    for (const editableField of EDITABLE_COMPANY_FIELD_DEFINITIONS) {
      if (includedEditableFields.has(editableField.key)) continue;
      if (editableField.editType === 'text') {
        const editableFieldValue = editableField.getValue(selectedCompany);
        rows.push({
          key: editableField.label,
          value: editableField.renderReadOnlyValue(editableFieldValue),
          editableFieldKey: editableField.key,
          editableFieldValue,
        });
      } else {
        const editableFieldArrayValue = editableField.getValue(selectedCompany);
        rows.push({
          key: editableField.label,
          value: editableField.renderReadOnlyValue(editableFieldArrayValue),
          editableFieldKey: editableField.key,
          editableFieldArrayValue,
        });
      }
    }

    return rows;
  }, [selectedCompany]);

  async function handleSaveCompanyField(
    fieldKey: EditableCompanyFieldKey,
    value: EditableFieldValue
  ): Promise<void> {
    const fieldDef = EDITABLE_COMPANY_FIELD_BY_KEY.get(fieldKey);
    if (!fieldDef || !selectedCompanyId) return;

    if (
      (fieldDef.editType === 'text' && typeof value !== 'string') ||
      (fieldDef.editType === 'multiselect-enum' && !Array.isArray(value))
    ) {
      throw new Error('Invalid value for selected field.');
    }

    const body =
      fieldDef.editType === 'text'
        ? fieldDef.buildMutationBody(selectedCompanyId, value as string)
        : fieldDef.buildMutationBody(selectedCompanyId, value as string[]);

    try {
      await saveCompanyMutation.mutateAsync({ body });
      await queryClient.query(companyGetOptions({ path: { id: selectedCompanyId } }));
    } catch (error) {
      let message = 'Unable to save field.';
      const detail = getProblemDetailMessage(error);
      if (detail) {
        message = `Unable to save field. ${detail}`;
      }
      const wrappedError = new Error(message) as Error & { cause?: unknown };
      wrappedError.cause = error;
      throw wrappedError;
    }
  }

  function handleSelectCompany(option: CompanyOption): void {
    hasInitializedSearchRef.current = true;
    setCompanySearch(option.name);
    setDebouncedCompanySearch(option.name);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(COMPANY_ID_PARAM, option.id);
    setSearchParams(nextParams, { replace: true });
  }

  function handleClearCompany(): void {
    hasInitializedSearchRef.current = false;
    setCompanySearch('');
    setDebouncedCompanySearch('');
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(COMPANY_ID_PARAM);
    setSearchParams(nextParams, { replace: true });
  }

  useEffect(() => {
    const activeSharedMetadataBookingEngine = toSharedMetadataTabBookingEngine(activeDetailsTab);
    if (!activeSharedMetadataBookingEngine) {
      return;
    }

    // Wait for company data before validating shared-metadata tab availability.
    if (selectedCompanyId && isSelectedCompanyLoading) {
      return;
    }

    if (!selectedCompany) {
      return;
    }

    if (!selectedCompanyBookingEngineTypes.includes(activeSharedMetadataBookingEngine)) {
      setActiveDetailsTab('COMPANY');
    }
  }, [
    activeDetailsTab,
    isSelectedCompanyLoading,
    selectedCompany,
    selectedCompanyBookingEngineTypes,
    selectedCompanyId,
  ]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(COMPANY_TAB_PARAM, activeDetailsTab);
    if (selectedCityId) {
      nextParams.set(COMPANY_CITY_ID_PARAM, selectedCityId);
    } else {
      nextParams.delete(COMPANY_CITY_ID_PARAM);
    }
    if (bookingEngineFilter) {
      nextParams.set(HOTELS_BOOKING_ENGINE_PARAM, bookingEngineFilter);
    } else {
      nextParams.delete(HOTELS_BOOKING_ENGINE_PARAM);
    }
    if (siteFilter) {
      nextParams.set(HOTELS_SITE_PARAM, siteFilter);
    } else {
      nextParams.delete(HOTELS_SITE_PARAM);
    }

    if (nextParams.toString() !== searchParams.toString()) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    activeDetailsTab,
    bookingEngineFilter,
    searchParams,
    selectedCityId,
    setSearchParams,
    siteFilter,
  ]);

  const detailsTabs = useMemo<
    Array<{ key: CompanyDetailsTabKey; label: string; bookingEngineType?: BookingEngineType }>
  >(() => {
    return [
      { key: 'COMPANY', label: 'Company' },
      { key: 'HOTELS', label: 'Hotels' },
      ...selectedCompanyBookingEngineTypes.map((bookingEngineType) => ({
        key: toSharedMetadataTabKey(bookingEngineType),
        label: `Shared Scrape Metadata: ${bookingEngineType}`,
        bookingEngineType,
      })),
    ];
  }, [selectedCompanyBookingEngineTypes]);

  const activeSharedMetadataBookingEngine = toSharedMetadataTabBookingEngine(activeDetailsTab);

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
      <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Companies</h1>

      <div className="mt-4 flex items-center gap-3">
        <Autocomplete
          id="company-search"
          value={companySearch}
          onValueChange={(value) => {
            setCompanySearch(value);
            if (!value) {
              hasInitializedSearchRef.current = false;
              const nextParams = new URLSearchParams(searchParams);
              nextParams.delete(COMPANY_ID_PARAM);
              setSearchParams(nextParams, { replace: true });
            }
          }}
          options={companyOptions}
          minSearchLength={MIN_COMPANY_SEARCH_LENGTH}
          searchLength={debouncedCompanySearch.length}
          placeholder="Search companies..."
          noResultsText="No matching companies."
          containerClassName="w-[400px] min-w-[200px] max-w-full"
          getOptionKey={(company) => company.id}
          renderOption={({ option }) => <>{option.name}</>}
          onSelectOption={handleSelectCompany}
        />

        {selectedCompanyId ? (
          <button
            type="button"
            onClick={handleClearCompany}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
          >
            Clear
          </button>
        ) : null}
      </div>

      {selectedCompanyId ? (
        <>
          <div className="mt-5">
            <h2 className="text-lg font-semibold leading-tight text-slate-900">
              {selectedCompany?.name ?? '—'} {`(${selectedCompany?.id})`}
            </h2>
          </div>

          <div className="mb-3 mt-3 flex items-center gap-2 border-b border-slate-200 pb-2">
            {detailsTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveDetailsTab(tab.key)}
                className={`rounded-md px-3 py-1 text-xs font-medium ${
                  activeDetailsTab === tab.key
                    ? 'bg-sky-100 text-sky-800'
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
            {activeDetailsTab === 'COMPANY' && isSelectedCompanyLoading ? (
              <p className="px-3 py-2 text-xs text-slate-600">Loading company...</p>
            ) : activeDetailsTab === 'COMPANY' && hasSelectedCompanyError ? (
              <p className="px-3 py-2 text-xs text-rose-700">
                {getApiErrorMessage(selectedCompanyError, 'Unable to load company.')}
              </p>
            ) : activeDetailsTab === 'COMPANY' && selectedCompany ? (
              <div>
                <EditableEntityTable
                  key={selectedCompanyId}
                  rows={detailRows}
                  fieldDefinitions={COMPANY_EDITABLE_FIELD_SPECS}
                  onSave={handleSaveCompanyField}
                />
              </div>
            ) : activeDetailsTab === 'HOTELS' ? (
              <div>
                <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 px-3 py-3">
                  <Autocomplete
                    id="city-search"
                    value={citySearch}
                    onValueChange={(value) => {
                      setCitySearch(value);
                      setSelectedCityId(null);
                    }}
                    options={cityOptions}
                    minSearchLength={MIN_CITY_SEARCH_LENGTH}
                    searchLength={debouncedCitySearch.length}
                    placeholder="Filter by city..."
                    noResultsText="No matching cities."
                    containerClassName="w-[420px] min-w-[220px] max-w-full"
                    getOptionKey={(city) => city.id}
                    renderOption={({ option }) => <>{option.label}</>}
                    onSelectOption={(city) => {
                      setSelectedCityId(city.id);
                      setCitySearch(city.label);
                    }}
                  />
                  <select
                    value={bookingEngineFilter}
                    onChange={(e) => {
                      setBookingEngineFilter(e.target.value as BookingEngineType | '');
                      setHotelsPage(1);
                    }}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                  >
                    <option value="">All booking engines</option>
                    {BOOKING_ENGINE_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <select
                    value={siteFilter}
                    onChange={(e) => {
                      setSiteFilter(e.target.value as HotelSite | '');
                      setHotelsPage(1);
                    }}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
                  >
                    <option value="">All sites</option>
                    {HOTEL_SITES.map((site) => (
                      <option key={site} value={site}>
                        {site}
                      </option>
                    ))}
                  </select>
                </div>

                {isHotelsLoading ? (
                  <p className="px-3 py-2 text-xs text-slate-600">Loading hotels...</p>
                ) : hasHotelsError ? (
                  <p className="px-3 py-2 text-xs text-rose-700">
                    {getApiErrorMessage(hotelsError, 'Unable to load hotels.')}
                  </p>
                ) : (
                  <PaginatedTable
                    columns={hotelsColumns}
                    rows={hotelsRows}
                    getRowKey={(hotel) => hotel.id ?? hotel.name ?? JSON.stringify(hotel)}
                    isLoading={false}
                    isError={false}
                    error={null}
                    loadingText="Loading hotels..."
                    errorText="Unable to load hotels."
                    emptyText="No hotels found for this company."
                    page={hotelsQueryResponse?.page ?? hotelsPage}
                    hasNextPage={hotelsHasNextPage}
                    isFetchingPage={isHotelsFetching}
                    onPreviousPage={() => {
                      setHotelsPage((previousPage) => Math.max(1, previousPage - 1));
                    }}
                    onNextPage={() => {
                      setHotelsPage(
                        hotelsHasNextPage ? (hotelsQueryResponse?.nextPage as number) : hotelsPage
                      );
                    }}
                    pageSize={hotelsPageSize}
                    pageSizeOptions={HOTELS_PAGE_SIZE_OPTIONS}
                    onPageSizeChange={(nextPageSize) => {
                      setHotelsPageSize(nextPageSize);
                      setHotelsPage(1);
                    }}
                  />
                )}
              </div>
            ) : activeSharedMetadataBookingEngine ? (
              <SharedScrapeMetadataPanel
                bookingEngineType={activeSharedMetadataBookingEngine}
                tableKeyPrefix={`${selectedCompanyId ?? 'company'}-`}
              />
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  );
}
